import { defineAction } from '~/prix'
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { attendanceShape } from '../../attendance/stats/attendanceStats.definition'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import medicalProcedureStatsAction from './medicalProcedureStats.action.json' with { type: 'json' }
import { dateOrIsoString } from '~/prix/types/date'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'


const sigtapShape = z.object({
  code: z.string().describe('Código SIGTAP'),
  description: z.string().describe('Descrição do procedimento'),
  male: z.boolean().describe('Masculino'),
  female: z.boolean().describe('Feminino'),
  outpatientService: z.number().describe('Valor ambulatorial'),
  hospitalService: z.number().describe('Valor hospitalar'),
  professionalService: z.number().describe('Valor profissional'),
  maximumAge: z.string().describe('Idade máxima'),
  minimumAge: z.string().describe('Idade mínima'),
})

export const medicalProcedureShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  attendanceOriginalId: z.string().describe('ID original do atendimento'),
  sigtapCode: z.string().describe('Código SIGTAP do procedimento'),
  procedureName: z.string().describe('Nome do procedimento'),
  isEvaluated: z.boolean().describe('Avaliado'),
  examResult: z.string().nullable().describe('Resultado do exame'),
  examResultDate: z.string().nullable().describe('Data do resultado do exame'),
  examRequestDate: z.string().nullable().describe('Data de solicitação do exame'),
  examPerformedDate: z.string().nullable().describe('Data de realização do exame'),
  observation: z.string().nullable().describe('Observação'),
  ...sigtapShape.shape,
  ...attendanceShape.omit({ originalId: true, dataSourceId: true }).shape,
})

const medicalProcedureExpression = expression(medicalProcedureShape.shape)

export const medicalProcedureVariable: Variable = {
  key: 'medicalProcedure',
  categoryKey: 'healthProcedures',
  actionDefinitionKey: medicalProcedureStatsAction.key,
  title: 'Exames médicos',
  unitSingular: 'exame',
  unitPlural: 'exames',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(medicalProcedureShape),
  filterableBy: getZodSchemaInfo(
    medicalProcedureShape.pick({
            /* CIDADÃO */
            'citizen.age': true,
            'citizen.sex': true,
            'citizen.deceased': true,
            /* SAÚDE */
            'citizenHealth.hasHypertension': true,
            'citizenHealth.hasDiabetes': true,
            'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
            'citizenHealth.isPregnant': true,
            'citizenHealth.smoker': true,
            'citizenHealth.skinColorOrRace': true,
            'citizenHealth.educationLevel': true,
            'citizenHealth.employmentStatus': true,
            'citizenHealth.homeless': true,
            'citizenHealth.hasPrivateHealthInsurance': true,
            'citizenHealth.nationality': true,
            /* ATENDIMENTO */
            'typeOfAttendance': true,
            'isVaccinationUpToDate': true,
            'bmi': true,
    })
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.medicalProcedure,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    'procedureName',
    'examResult',
    'observation',
    'examResultDate',
    'examRequestDate',
    'examPerformedDate',
    /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.originalId',
    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
    /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'teamName',
    'weight',
    'height',
    'typeOfAttendance',
    'isVaccinationUpToDate',
    'lastMenstruationDate',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfWeek', degree: 0 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 0 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 0 },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
    ],
  },
  assertions: prepareAssertions(medicalProcedureExpression),
}

const medicalProcedureStatsDefinition = defineAction({
  ...medicalProcedureStatsAction,
  input: z.object({
    variable,
    highlight: medicalProcedureExpression.optional(),
    filter: medicalProcedureExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(medicalProcedureShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default medicalProcedureStatsDefinition
